// Picking the line geometry a 3D flyover should fly along, out of a hike's GeoJSON.
//
// A Hike has no single line: its geometry is its planned trails' line strings, delivered as
// MultiLineString features alongside start/end Points. The flyover needs the LINES, split into the
// runs it animates — and it has to survive a hike whose routes-only geometry comes back empty, which
// is what left the flyover an empty map with no stats on it at all.

export interface LineSegmentFeature {
  type: 'Feature';
  geometry: { type: 'LineString'; coordinates: number[][] };
  properties: Record<string, unknown>;
}

/** Tolerates a JSON string, a parsed object, null, or malformed input — never throws. */
export function parseFeatureCollection(input: unknown): Record<string, any> | null {
  if (input == null) return null;
  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }
  return typeof input === 'object' ? (input as Record<string, any>) : null;
}

/**
 * Every animatable run of line geometry in a feature collection, in the order it is laid down: each
 * MultiLineString split into its own LineStrings (they need not meet — see the Ruby RouteStitcher),
 * each LineString kept as it is, and everything else (Points, Polygons) left out. Runs of fewer than
 * two vertices are dropped: nothing can be animated along them, and they used to reach the renderer.
 *
 * Replaces the inline filtering in FlyoverPreview, which read `geojson.features || [geojson]` and then
 * `features[0].geometry` — an EMPTY array is truthy, so the `|| [geojson]` fallback never fired and
 * that second line threw `TypeError: Cannot read properties of undefined (reading 'geometry')`. The
 * throw aborted the render before it set the coordinates the elevation profile and the live
 * Distance/Elevation/Grade row are gated on, so the flyover showed a map with no route and no stats.
 */
export function lineSegmentsFrom(featureCollection: unknown): LineSegmentFeature[] {
  const fc = parseFeatureCollection(featureCollection);
  if (!fc) return [];

  const features = Array.isArray(fc.features) ? fc.features : [fc];
  const segments: LineSegmentFeature[] = [];
  const push = (coordinates: unknown, properties: Record<string, unknown>) => {
    if (!Array.isArray(coordinates) || coordinates.length < 2) return;
    segments.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: coordinates as number[][] },
      properties: { ...properties, segmentIndex: segments.length },
    });
  };

  features.forEach((feature: any) => {
    const geometry = feature && feature.geometry;
    if (!geometry) return;
    const properties = (feature.properties || {}) as Record<string, unknown>;

    if (geometry.type === 'MultiLineString' && Array.isArray(geometry.coordinates)) {
      geometry.coordinates.forEach((run: unknown) => push(run, properties));
    } else if (geometry.type === 'LineString') {
      push(geometry.coordinates, properties);
    }
  });

  return segments;
}

/**
 * The runs a hike's flyover should animate: its ROUTES-ONLY geometry, falling back to its full
 * geometry when that carries no lines at all.
 *
 * `Hike#geojson_routes_only` keeps only planned trails styled 'route' (or unstyled), so a hike whose
 * trails are all alternates, spurs or transport links filters down to zero features — as does a hike
 * with no planned trails yet. Without the fallback there was no line to animate, so the flyover was an
 * empty map. Ruby's Trailer::ConfigBuilder already makes exactly this choice for the same reason: a
 * hike with no route-styled geometry still has to render, so it walks every style rather than nothing.
 */
export function hikeFlyoverSegments(
  routesOnly: unknown,
  fullGeometry: unknown,
): { segments: LineSegmentFeature[]; usedFallback: boolean } {
  const routeSegments = lineSegmentsFrom(routesOnly);
  if (routeSegments.length > 0) return { segments: routeSegments, usedFallback: false };

  const fallback = lineSegmentsFrom(fullGeometry);
  return { segments: fallback, usedFallback: fallback.length > 0 };
}
